import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import i18n from '@/i18n';
import {
  authApi,
  authKeys,
  phase2Api,
  type AsyncAuthAction,
  type AuthSession,
  type CompleteAuth,
  type RealAccount,
  type RealEntitlements,
  type RealSettings,
} from '@/lib/api/auth';
import { AUTH_EXPIRED_EVENT, HttpError } from '@/lib/api/http';
import { MOCK_DATA } from '@/lib/api/mode';
import { setCsrf } from '@/lib/api/session';

type Phase2Data = {
  account: RealAccount;
  entitlements: RealEntitlements;
  settings: RealSettings;
};

type AuthState =
  | { status: 'checking' }
  | { status: 'anonymous'; notice?: 'expired' }
  | { status: 'error'; kind: 'offline' | 'service' }
  | { status: 'authenticated'; session: AuthSession; phase2: Phase2Data | null; dev: boolean };

type AuthContextValue = {
  state: AuthState;
  complete: CompleteAuth;
  logout: AsyncAuthAction;
  retry: AsyncAuthAction;
  incomingLinkToken: string | null;
  clearIncomingLink: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function devAuthBypass() {
  return MOCK_DATA;
}

function readIncomingLinkToken() {
  if (typeof window === 'undefined' || !window.location.hash) return null;
  const values = new URLSearchParams(window.location.hash.slice(1));
  const token = values.get('token');
  if (!token || token.length < 20 || token.length > 200) return null;
  return token;
}

function removeIncomingLinkTokenFromUrl() {
  if (typeof window === 'undefined' || !window.location.hash) return;
  const values = new URLSearchParams(window.location.hash.slice(1));
  if (!values.has('token')) return;
  values.delete('token');
  const hash = values.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ''}`);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const bypass = useMemo(devAuthBypass, []);
  const [incomingLinkToken, setIncomingLinkToken] = useState(readIncomingLinkToken);
  const authRun = useRef(0);
  const [state, setState] = useState<AuthState>(() => bypass
    ? {
        status: 'authenticated',
        session: { authenticated: true, expires_at: '2099-01-01T00:00:00Z', csrf_token: 'development-csrf' },
        phase2: null,
        dev: true,
      }
    : { status: 'checking' });

  const clearAccountQueries = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  const hydrate = useCallback(async (): Promise<Phase2Data> => {
    const [account, entitlements, settings] = await Promise.all([
      queryClient.fetchQuery({ queryKey: authKeys.account, queryFn: phase2Api.account, staleTime: 0 }),
      queryClient.fetchQuery({ queryKey: authKeys.entitlements, queryFn: phase2Api.entitlements, staleTime: 0 }),
      queryClient.fetchQuery({ queryKey: authKeys.settings, queryFn: phase2Api.settings, staleTime: 0 }),
    ]);
    if (i18n.language !== account.language) await i18n.changeLanguage(account.language);
    return { account, entitlements, settings };
  }, [queryClient]);

  const complete = useCallback(async (session: AuthSession) => {
    const run = ++authRun.current;
    setState({ status: 'checking' });
    queryClient.setQueryData(authKeys.me, session);
    try {
      const phase2 = await hydrate();
      if (run !== authRun.current) return;
      setState({ status: 'authenticated', session, phase2, dev: false });
    } catch (error) {
      if (run !== authRun.current) return;
      if (error instanceof HttpError && error.code === 'unauthenticated') {
        clearAccountQueries();
        setState({ status: 'anonymous', notice: 'expired' });
        return;
      }
      setState({ status: 'error', kind: error instanceof HttpError && error.kind === 'offline' ? 'offline' : 'service' });
    }
  }, [clearAccountQueries, hydrate, queryClient]);

  const restore = useCallback(async () => {
    if (bypass) return;
    const run = ++authRun.current;
    setState({ status: 'checking' });
    try {
      const session = await authApi.me();
      if (run !== authRun.current) return;
      queryClient.setQueryData(authKeys.me, session);
      const phase2 = await hydrate();
      if (run !== authRun.current) return;
      setState({ status: 'authenticated', session, phase2, dev: false });
    } catch (error) {
      if (run !== authRun.current) return;
      if (error instanceof HttpError && error.code === 'unauthenticated') {
        clearAccountQueries();
        setState({ status: 'anonymous' });
        return;
      }
      setState({ status: 'error', kind: error instanceof HttpError && error.kind === 'offline' ? 'offline' : 'service' });
    }
  }, [bypass, clearAccountQueries, hydrate, queryClient]);

  useEffect(() => {
    removeIncomingLinkTokenFromUrl();
    void restore();
  }, [restore]);

  useEffect(() => {
    if (bypass) return;
    const expired = () => {
      authRun.current += 1;
      clearAccountQueries();
      setState({ status: 'anonymous', notice: 'expired' });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, expired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, expired);
  }, [bypass, clearAccountQueries]);

  const logout = useCallback(async () => {
    if (state.status !== 'authenticated') return;
    if (!state.dev) {
      try {
        await authApi.logout(state.session.csrf_token);
      } catch (error) {
        if (!(error instanceof HttpError && error.code === 'unauthenticated')) throw error;
      }
    }
    authRun.current += 1;
    clearAccountQueries();
    setIncomingLinkToken(null);
    setState({ status: 'anonymous' });
  }, [clearAccountQueries, state]);

  // the session's CSRF token for state-changing API calls (memory only)
  useEffect(() => {
    setCsrf(state.status === 'authenticated' && !state.dev ? state.session.csrf_token : null);
  }, [state]);

  const value = useMemo<AuthContextValue>(() => ({
    state,
    complete,
    logout,
    retry: restore,
    incomingLinkToken,
    clearIncomingLink: () => setIncomingLinkToken(null),
  }), [complete, incomingLinkToken, logout, restore, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}
